import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_ADMIN_ACCESS } from 'actions/adminAccess';
import { Modal, Spinner } from 'react-bootstrap';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/inputs/Button';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import { AiOutlinePlus } from 'react-icons/ai';
import { FiUser, FiMail, FiPhone, FiLock, FiShield, FiGlobe, FiCheckCircle, FiArrowLeft, FiCopy } from 'react-icons/fi';

const ADMIN_ROLES = [
  { label: 'Tenant Admin', value: 'tenant_admin' },
  { label: 'Support Admin', value: 'support_admin' },
  { label: 'Super Admin', value: 'super_admin' },
  { label: 'Network Admin', value: 'network_admin' },
  { label: 'ZMT Admin', value: 'ZMT_admin' },
  { label: 'Admin Fleet', value: 'Admin_fleet' },
  { label: 'Finance Admin', value: 'finance_admin' },
];

const initialFormData = {
  role: '',
  phone: '',
  name: '',
  username: '',
  email: '',
  password: '',
  country_code: 91,
};

const styles = {
  modalHeader: {
    background: '#1b2a38',
    color: '#fff',
    borderBottom: '1px solid #3b4a56',
    padding: '20px 24px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepBadge: {
    backgroundColor: '#be210b',
    padding: '3px 10px',
    borderRadius: '15px',
    fontSize: '11px',
    fontWeight: '600',
  },
  modalBody: {
    padding: '24px',
    backgroundColor: '#223343',
  },
  inputGroup: {
    marginBottom: '18px',
  },
  inputLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#B9C8D3',
    marginBottom: '6px',
  },
  inputIcon: {
    color: '#be210b',
    flexShrink: 0,
  },
  inputField: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #3b4a56',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#ffffff',
    backgroundColor: '#1b2a38',
    outline: 'none',
  },
  selectField: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #3b4a56',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#ffffff',
    backgroundColor: '#1b2a38',
    outline: 'none',
    cursor: 'pointer',
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #3b4a56',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    backgroundColor: '#223343',
  },
  cancelBtn: {
    backgroundColor: '#29343e',
    color: '#B9C8D3',
    border: '1px solid #3b4a56',
    padding: '9px 20px',
    borderRadius: '15px',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  primaryBtn: {
    backgroundColor: '#be210b',
    color: '#fff',
    border: 'none',
    padding: '9px 22px',
    borderRadius: '15px',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  disabledBtn: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  reviewCard: {
    backgroundColor: '#1b2a38',
    borderRadius: '10px',
    border: '1px solid #3b4a56',
    overflow: 'hidden',
  },
  reviewRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 18px',
    borderBottom: '1px solid #3b4a56',
  },
  reviewRowLast: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 18px',
  },
  reviewIcon: {
    width: '34px',
    height: '34px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    flexShrink: 0,
    backgroundColor: '#29343e',
  },
  reviewLabel: {
    fontSize: '11px',
    fontWeight: '500',
    color: '#8d949b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '1px',
  },
  reviewValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#be210b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};

const AdminAccess = () => {
  const dispatch = useDispatch();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [copied, setCopied] = useState(false);

  const isLoader = useSelector((state) => state.adminAccess.isLoading);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid =
    formData.role && formData.phone && formData.name && formData.username && formData.email && formData.password;

  const handleReview = () => {
    setShowReview(true);
  };

  const handleBackToForm = () => {
    setShowReview(false);
  };

  const handleCreateAdmin = () => {
    dispatch({
      type: CREATE_ADMIN_ACCESS.REQUEST,
      payload: formData,
      cb: (res) => {
        if (res.status === 201) {
          setShowCreateModal(false);
          setShowReview(false);
          setFormData(initialFormData);
        }
      },
    });
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowReview(false);
    setFormData(initialFormData);
    setCopied(false);
  };

  const getRoleLabel = (value) => {
    const role = ADMIN_ROLES.find((r) => r.value === value);
    return role ? role.label : value;
  };

  const handleCopyDetails = () => {
    const text = `Dear ${formData.name},

Your user account has been created successfully. Please find your login details below:

Role: ${getRoleLabel(formData.role)}
Full Name: ${formData.name}
Username: ${formData.username}
Email Address: ${formData.email}
Phone Number: +${formData.country_code} ${formData.phone}
Password: ${formData.password}
URL: https://app.chargecloud.net/

Please use the above credentials to log in to the system. For security reasons, we recommend changing your password after your first login.

If you face any issues accessing your account, please contact the support team.

Best regards,
CZ IT Team`;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const ReviewRow = ({ icon: Icon, label, value, isLast }) => (
    <div style={isLast ? styles.reviewRowLast : styles.reviewRow}>
      <div style={styles.reviewIcon}>
        <Icon size={16} color="#be210b" />
      </div>
      <div>
        <div style={styles.reviewLabel}>{label}</div>
        <div style={styles.reviewValue}>{value}</div>
      </div>
    </div>
  );

  ReviewRow.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    isLast: PropTypes.bool,
  };

  return (
    <LayoutContainer className="layout-container-main-page">
      <AdminHeader title="Admin Access" />
      <div className="page-content-wrapper scrollable">
        <div className="data-report--main">
          <Card>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '15px',
                  backgroundColor: '#be210b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '18px',
                }}
              >
                <FiShield size={28} color="#fff" />
              </div>
              <h4 style={{ marginBottom: '8px', fontWeight: '600' }}>Admin Access Management</h4>
              <p style={{ color: '#8d949b', marginBottom: '28px', textAlign: 'center', maxWidth: '400px', fontSize: '14px' }}>
                Create admin users with roles such as Tenant Admin, Support Admin, Network Admin, and more.
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <span className="me-2">
                  <AiOutlinePlus color={'#FFFFFF'} />
                </span>
                Create Admin Access
              </Button>
            </div>

            {/* Create Admin Access Modal */}
            {showCreateModal && (
              <Modal
                show={showCreateModal}
                centered
                onHide={handleCloseModal}
                size="lg"
                contentClassName="border-0"
              >
                <div style={styles.modalHeader}>
                  <div style={styles.modalTitle}>
                    {showReview ? <FiCheckCircle size={20} /> : <FiShield size={20} />}
                    <span>{showReview ? 'Review Details' : 'Create Admin Access'}</span>
                    <span style={styles.stepBadge}>{showReview ? 'Step 2/2' : 'Step 1/2'}</span>
                  </div>
                </div>

                <div style={styles.modalBody}>
                  {!showReview ? (
                    <>
                      <div style={styles.sectionTitle}>
                        <FiUser size={13} />
                        Personal Information
                      </div>
                      <Row>
                        <Col md={6}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-role" style={styles.inputLabel}>
                              <FiShield size={13} style={styles.inputIcon} />
                              Role *
                            </label>
                            <select
                              id="admin-role"
                              style={styles.selectField}
                              name="role"
                              value={formData.role}
                              onChange={handleInputChange}
                            >
                              <option value="">Select a role...</option>
                              {ADMIN_ROLES.map((role) => (
                                <option key={role.value} value={role.value}>
                                  {role.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-name" style={styles.inputLabel}>
                              <FiUser size={13} style={styles.inputIcon} />
                              Full Name *
                            </label>
                            <input
                              id="admin-name"
                              type="text"
                              style={styles.inputField}
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="e.g. John Doe"
                            />
                          </div>
                        </Col>
                      </Row>

                      <div style={{ ...styles.sectionTitle, marginTop: '6px' }}>
                        <FiMail size={13} />
                        Account Details
                      </div>
                      <Row>
                        <Col md={6}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-username" style={styles.inputLabel}>
                              <FiUser size={13} style={styles.inputIcon} />
                              Username *
                            </label>
                            <input
                              id="admin-username"
                              type="text"
                              style={styles.inputField}
                              name="username"
                              value={formData.username}
                              onChange={handleInputChange}
                              placeholder="e.g. johndoe"
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-email" style={styles.inputLabel}>
                              <FiMail size={13} style={styles.inputIcon} />
                              Email Address *
                            </label>
                            <input
                              id="admin-email"
                              type="email"
                              style={styles.inputField}
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="e.g. john@company.com"
                            />
                          </div>
                        </Col>
                      </Row>

                      <div style={{ ...styles.sectionTitle, marginTop: '6px' }}>
                        <FiLock size={13} />
                        Contact &amp; Security
                      </div>
                      <Row>
                        <Col md={3}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-country-code" style={styles.inputLabel}>
                              <FiGlobe size={13} style={styles.inputIcon} />
                              Code
                            </label>
                            <input
                              id="admin-country-code"
                              type="number"
                              style={styles.inputField}
                              name="country_code"
                              value={formData.country_code}
                              onChange={handleInputChange}
                              placeholder="91"
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-phone" style={styles.inputLabel}>
                              <FiPhone size={13} style={styles.inputIcon} />
                              Phone Number *
                            </label>
                            <input
                              id="admin-phone"
                              type="text"
                              style={styles.inputField}
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="e.g. 9876543210"
                            />
                          </div>
                        </Col>
                        <Col md={5}>
                          <div style={styles.inputGroup}>
                            <label htmlFor="admin-password" style={styles.inputLabel}>
                              <FiLock size={13} style={styles.inputIcon} />
                              Password *
                            </label>
                            <input
                              id="admin-password"
                              type="text"
                              style={styles.inputField}
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder="Enter a strong password"
                            />
                          </div>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <p style={{ color: '#B9C8D3', marginBottom: '18px', fontSize: '14px' }}>
                        Please confirm the details below are correct before creating this admin account.
                      </p>
                      <div style={styles.reviewCard}>
                        <ReviewRow icon={FiShield} label="Role" value={getRoleLabel(formData.role)} />
                        <ReviewRow icon={FiUser} label="Full Name" value={formData.name} />
                        <ReviewRow icon={FiUser} label="Username" value={formData.username} />
                        <ReviewRow icon={FiMail} label="Email Address" value={formData.email} />
                        <ReviewRow
                          icon={FiPhone}
                          label="Phone Number"
                          value={`+${formData.country_code} ${formData.phone}`}
                        />
                        <ReviewRow
                          icon={FiLock}
                          label="Password"
                          value={formData.password}
                          isLast
                        />
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.cancelBtn,
                          marginTop: '14px',
                          width: '100%',
                          justifyContent: 'center',
                          backgroundColor: copied ? '#0f9b72' : '#29343e',
                          color: copied ? '#fff' : '#B9C8D3',
                          borderColor: copied ? '#0f9b72' : '#3b4a56',
                        }}
                        onClick={handleCopyDetails}
                      >
                        <FiCopy size={14} />
                        {copied ? 'Copied to Clipboard!' : 'Copy Details'}
                      </button>
                    </>
                  )}
                </div>

                <div style={styles.modalFooter}>
                  {!showReview ? (
                    <>
                      <button type="button" style={styles.cancelBtn} onClick={handleCloseModal}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        style={{ ...styles.primaryBtn, ...(isFormValid ? {} : styles.disabledBtn) }}
                        onClick={handleReview}
                        disabled={!isFormValid}
                      >
                        Review Details
                        <FiCheckCircle size={15} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" style={styles.cancelBtn} onClick={handleBackToForm}>
                        <FiArrowLeft size={14} />
                        Back to Edit
                      </button>
                      <button
                        type="button"
                        style={{ ...styles.primaryBtn, ...(isLoader ? styles.disabledBtn : {}) }}
                        onClick={handleCreateAdmin}
                        disabled={isLoader}
                      >
                        {isLoader ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" />
                            Creating...
                          </>
                        ) : (
                          <>
                            Create Admin
                            <FiCheckCircle size={15} />
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </Modal>
            )}
          </Card>
        </div>
      </div>
    </LayoutContainer>
  );
};

export default AdminAccess;
